import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PermissionGuard } from "@/components/auth/PermissionGuard";
import { FeatureGuard } from "@/components/auth/FeatureGuard";
import { AuthCallback } from "@/components/auth/AuthCallback";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

// Pages
import { LoginPage } from "@/pages/LoginPage";
import { SignupPage } from "@/pages/SignupPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import BillingRequiredPage from "@/pages/BillingRequiredPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { LeadsListPage } from "@/pages/LeadsListPage";
import { LeadsPipelinePage } from "@/pages/LeadsPipelinePage";
import { LeadDetailPage } from "@/pages/LeadDetailPage";
import { FormsListPage } from "@/pages/FormsListPage";
import { FormBuilderPage } from "@/pages/FormBuilderPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { LandingPagesListPage } from "@/pages/LandingPagesListPage";
import { LandingPageBuilderPage } from "@/pages/LandingPageBuilderPage";
import { PublicFormPage } from "@/pages/PublicFormPage";
import { AdminPage } from "@/pages/AdminPage";
import { SeoAgentPage } from "@/pages/SeoAgentPage";
import TimeTrackingPage from "@/pages/TimeTrackingPage";
import { ClientsListPage } from "@/pages/ClientsListPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import { EmployeesListPage } from "@/pages/EmployeesListPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import { SubcontractorsListPage } from "@/pages/SubcontractorsListPage";
import SubcontractorDetailPage from "@/pages/SubcontractorDetailPage";
import MobileLogin from "@/pages/mobile/MobileLogin";
import MobileHome from "@/pages/mobile/MobileHome";
import MobileWorksite from "@/pages/mobile/MobileWorksite";

// Legal Pages (Public)
import { TermsPage } from "@/pages/legal/TermsPage";
import { PrivacyPage } from "@/pages/legal/PrivacyPage";

// Public SEO landing pages (bilingual, strictly separated by URL)
import AiLeadGenPage from "@/pages/seo-landing/AiLeadGenPage";

// Public fallback for invalid form slugs — must NEVER redirect to /login or /dashboard.
// Rendered when a form slug under /embed/* or /soumission/* does not match the
// :formSlug route (e.g. extra path segment, typo) so iframes never fall through
// to the catch-all that redirects to /dashboard.
function PublicFormNotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-2xl font-bold tracking-tight">Formulaire introuvable</h1>
        <p className="text-sm text-muted-foreground">
          Ce formulaire n'existe pas ou n'est plus disponible. Veuillez contacter l'entreprise pour obtenir un nouveau lien.
        </p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id BEFORE any other routing
  // This must be synchronous to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      {/* Public form routes — must NEVER be guarded. /embed/* is the preferred
          path for external iframe integrations; /soumission/* kept for backward
          compatibility. The wildcard fallbacks guarantee that any unknown
          subpath under these prefixes shows a public "Formulaire introuvable"
          page instead of falling through to the catch-all that redirects to
          /dashboard (which would force authentication). */}
      <Route path="/embed/:formSlug" element={<PublicFormPage />} />
      <Route path="/embed/*" element={<PublicFormNotFound />} />
      <Route path="/soumission/:formSlug" element={<PublicFormPage />} />
      <Route path="/soumission/*" element={<PublicFormNotFound />} />

      {/* Mobile PWA */}
      <Route path="/mobile" element={<MobileLogin />} />
      <Route path="/mobile/home" element={<MobileHome />} />
      <Route path="/mobile/worksite/:clientId" element={<MobileWorksite />} />
      
      {/* Legal Pages (Public - No Auth Required) */}
      <Route path="/fr/conditions-dutilisation" element={<TermsPage />} />
      <Route path="/en/terms-of-use" element={<TermsPage />} />
      <Route path="/fr/politique-de-confidentialite" element={<PrivacyPage />} />
      <Route path="/en/privacy-policy" element={<PrivacyPage />} />

      {/* Public SEO landing pages — bilingual, NEVER mixed on the same URL.
          Both with and without the trailing slash resolve to the same page
          so old / new sitemap entries always return HTTP 200. */}
      <Route path="/en/ai-lead-generation-software" element={<AiLeadGenPage locale="en" />} />
      <Route path="/en/ai-lead-generation-software/" element={<AiLeadGenPage locale="en" />} />
      <Route path="/fr/logiciel-generation-leads-ia" element={<AiLeadGenPage locale="fr" />} />
      <Route path="/fr/logiciel-generation-leads-ia/" element={<AiLeadGenPage locale="fr" />} />
      
      {/* Onboarding */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } 
      />

      {/* Paywall — required after signup, before any dashboard access. */}
      <Route
        path="/billing/required"
        element={
          <ProtectedRoute>
            <BillingRequiredPage />
          </ProtectedRoute>
        }
      />
      
      {/* Protected Dashboard Routes (pathless parent so "/" stays public) */}
      <Route 
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Leads */}
        <Route path="leads" element={<LeadsListPage />} />
        <Route path="leads/pipeline" element={<LeadsPipelinePage />} />
        <Route path="leads/:leadId" element={<LeadDetailPage />} />
        
        {/* Clients */}
        <Route path="clients" element={<ClientsListPage />} />
        <Route path="clients/:clientId" element={<ClientDetailPage />} />
        
        {/* Employees */}
        <Route path="employees" element={<PermissionGuard permission="can_manage_employees"><FeatureGuard feature="multi_user"><EmployeesListPage /></FeatureGuard></PermissionGuard>} />
        <Route path="employees/:employeeId" element={<PermissionGuard permission="can_manage_employees"><FeatureGuard feature="multi_user"><EmployeeDetailPage /></FeatureGuard></PermissionGuard>} />
        {/* Subcontractors */}
        <Route path="subcontractors" element={<PermissionGuard permission="can_manage_subcontractors"><FeatureGuard feature="multi_user"><SubcontractorsListPage /></FeatureGuard></PermissionGuard>} />
        <Route path="subcontractors/:subcontractorId" element={<PermissionGuard permission="can_manage_subcontractors"><FeatureGuard feature="multi_user"><SubcontractorDetailPage /></FeatureGuard></PermissionGuard>} />
        
        {/* Forms */}
        <Route path="forms" element={<PermissionGuard permission="can_manage_forms"><FormsListPage /></PermissionGuard>} />
        <Route path="forms/new" element={<PermissionGuard permission="can_manage_forms"><FormBuilderPage /></PermissionGuard>} />
        <Route path="forms/:formId" element={<PermissionGuard permission="can_manage_forms"><FormBuilderPage /></PermissionGuard>} />
        
        {/* Landing Pages */}
        <Route path="landing-pages" element={<PermissionGuard permission="can_manage_pages"><LandingPagesListPage /></PermissionGuard>} />
        <Route path="landing-pages/new" element={<PermissionGuard permission="can_manage_pages"><LandingPageBuilderPage /></PermissionGuard>} />
        <Route path="landing-pages/:pageId" element={<PermissionGuard permission="can_manage_pages"><LandingPageBuilderPage /></PermissionGuard>} />
        
        {/* Settings */}
        <Route path="settings" element={<PermissionGuard permission="can_manage_settings"><SettingsPage /></PermissionGuard>} />
        
        {/* SEO Agent */}
        {/* SEO (Marketing IA) — Plan-gated only. The sidebar visibility
            already uses `seo_articles` (plan feature) as the source of
            truth, and the backend's _admin_auth enforces the same
            feature gate. Using PermissionGuard here would silently
            redirect Enterprise owners (role=admin_enterprise, which
            does not carry can_manage_seo) back to /dashboard,
            making submenu clicks appear to do nothing. */}
        <Route path="seo" element={<FeatureGuard feature="seo_articles"><SeoAgentPage /></FeatureGuard>} />
        
        {/* Time Tracking */}
        <Route path="timetracking" element={<PermissionGuard permission="can_manage_employees"><FeatureGuard feature="multi_user"><TimeTrackingPage /></FeatureGuard></PermissionGuard>} />
        
        {/* Admin Panel (admin only) */}
        <Route path="admin" element={<AdminPage />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
